import i18n from '@/web/utils/localization';
import { TranslateResult } from 'vue-i18n';

interface PlanData {
  id: number;
  isMostPopular: boolean;
  name: string;
  description: string;
  monthlyAmount: string;
  fullAmountText: string;
  amountDescription: string;
  amountChange: number;
  payments: string;
  planList: Array<string>;
  isActive: boolean;
  status: string;
}

function yearlyPerMonth(plan) {
  let yearlyPerMonth;
  if (plan.number_of_installments > 1) {
    yearlyPerMonth = `${plan.yearly_amount / plan.number_of_installments}`;
  } else {
    yearlyPerMonth = plan.yearly_amount / 12;
  }
  return Math.round(yearlyPerMonth);
}

function getPlanFeaturesList(plan: PlanResponse): Array<TranslateResult> {
  const featuresList: Array<TranslateResult> = [];
  featuresList.push(
    i18n.tc('SIGNUP.TEXT.NUMBER_INSTRUCTORS_INCLUDED', plan.teachers, {
      number: plan.teachers,
    })
  );
  // featuresList.push(
  //   i18n.t('SIGNUP.TEXT.UP_TO_NUM_STUDENTS', {
  //     number: plan.student_accounts,
  //   })
  // );
  featuresList.push(i18n.t('SIGNUP.TEXT.UNLIMITED_ZOOM_STREAMING'));
  // featuresList.push(
  //   i18n.t('SIGNUP.TEXT.NUMBER_MINS_STREAMING', {
  //     number: plan.streaming_minutes,
  //   })
  // );
  if (plan.allows_custom_domain) i18n.t('SIGNUP.TEXT.CUSTOM_DOMAIN');
  return featuresList;
}

function getPlansData(
  plan: PlanResponse,
  subscriptionType: string,
  selectedPlanId?: number,
  freeTrial?: boolean,
  freeTrialDays?: number,
  selectedSubscriptionType?: string,
  couponDuration?: CouponDurationResponse
): PlanData {
  if (!selectedSubscriptionType) {
    selectedSubscriptionType = subscriptionType;
  }
  const freeTrialInsert = freeTrial
    ? i18n.t('STUDIO.TITLE.PLAN_FREE_TRIAL_THEN', { days: freeTrialDays })
    : '';
  const data = {
    monthly: {
      amountKey: 'SIGNUP.TEXT.AMOUNT_MONTH',
      paidKey: 'SIGNUP.TEXT.PAID_MONTHLY',
      amount: Math.max(Math.round(plan.monthly_amount), 0),
      amount_num: Math.max(Math.round(plan.monthly_amount), 0),
      monthlyAmount: i18n.t('SIGNUP.TEXT.PER_MO', {
        per: '$' + Math.max(Math.round(plan.monthly_amount), 0),
      }),
      title: i18n.t('STUDIO.TITLE.PLAN_AMOUNT_MONTH_PAID_MONTHLY', {
        plan: plan.display_name ?? plan.plan_name,
        amount: '$' + Math.max(Math.round(plan.monthly_amount), 0),
        freeTrialInsert,
      }),
      standard: plan.monthly_standard,
      couponDurationType: couponDuration?.duration,
      couponDurationMonths: couponDuration?.duration_in_months,
      discounted: plan.monthly_discounted,
    },
    yearly: {
      amountKey: 'SIGNUP.TEXT.AMOUNT_YEAR',
      paidKey: 'SIGNUP.TEXT.PAID_ANNUALLY',
      amount: Math.max(Math.round(plan.yearly_amount), 0),
      amount_num: Math.max(Math.round(plan.yearly_amount), 0),
      monthlyAmount: i18n.t('SIGNUP.TEXT.PER_MO', {
        per: '$' + Math.max(yearlyPerMonth(plan), 0),
      }),
      title: i18n.t('STUDIO.TITLE.PLAN_AMOUNT_MONTH_PAID_ANNUALLY', {
        plan: plan.display_name ?? plan.plan_name,
        amount: '$' + Math.max(plan.yearly_amount, 0),
        freeTrialInsert,
      }),
      standard: plan.yearly_standard,
      couponDurationType: couponDuration?.duration,
      couponDurationMonths: couponDuration?.duration_in_months,
      discounted: plan.yearly_discounted,
    },
  };

  const paymentsText = i18n.tc(
    'SIGNUP.TEXT.MONTHLY_PAYMENTS',
    plan.number_of_installments,
    { count: plan.number_of_installments }
  );

  const {
    amountKey,
    paidKey,
    amount,
    amount_num,
    monthlyAmount,
    title,
    discounted,
    couponDurationType,
    couponDurationMonths,
    standard,
  } = data[subscriptionType];

  return {
    id: plan.id,
    amount: amount_num,
    amountKey: amountKey,
    key: plan.plan_name.toLowerCase(),
    isMostPopular: plan.is_default,
    name: plan.plan_name,
    displayName: plan.display_name || plan.plan_name,
    description: plan.description,
    monthlyAmount: monthlyAmount,
    fullAmountText: i18n.t(amountKey, { amount }),
    amountDescription: i18n.t(paidKey),
    amountChange: Math.max(
      Math.floor(plan.monthly_amount * 12 - plan.yearly_amount),
      0
    ),
    payments: paymentsText,
    planList: getPlanFeaturesList(plan),
    isActive:
      selectedPlanId === plan.id &&
      subscriptionType === selectedSubscriptionType,
    status: 'select',
    isFreeTrial: freeTrial,
    freeTrialDays: freeTrialDays,
    title,
    discounted,
    couponDurationType,
    couponDurationMonths,
    standard,
    subscriptionType,
  } as PlanData;
}

function getCoachingProgrammePlansData(plan) {
  const data = {
    yearly: {
      amountKey: 'SIGNUP.TEXT.AMOUNT_YEAR',
      amount: '$' + Math.max(Math.round(plan.yearly_amount), 0),
      monthlyAmount: i18n.t('SIGNUP.TEXT.PER_MO', {
        per: '$' + Math.max(yearlyPerMonth(plan), 0),
      }),
    },
  };

  const { amountKey, amount } = data.yearly;

  return {
    id: plan.id,
    option_id: plan.option_id,
    isMostPopular: plan.is_default,
    name: plan.plan_name,
    displayName: plan.display_name || plan.plan_name,
    description: plan.description,
    // @ts-ignore
    fullAmountText: plan.is_payment_plan
      ? `$${plan.initial_payment_amount}`
      : i18n.t(amountKey, { amount }),
    isActive: plan.is_default,
    status: 'select',
    // @ts-ignore
    isPaymentPlan: plan.is_payment_plan,
    // @ts-ignore
    paymentPlanId: plan.payment_plan_id,
    // @ts-ignore
    couponCode: plan.couponCode,
  };
}

function getUpgradePlansData(
  plan: PlanResponse,
  subscriptionType: string,
  teacher: TeacherDetailResponse,
  couponDuration?: CouponDurationResponse
): PlanData {
  const planData = getPlansData(
    plan,
    subscriptionType,
    undefined,
    undefined,
    undefined,
    undefined,
    couponDuration
  );

  const intervalMonthsMap = {
    monthly: 1,
    yearly: 12,
  };
  const subscription_plan_id = teacher.subscription_plan.id;
  const subscription_plan_tier = teacher.subscription_plan.tier;
  const subscription_interval = teacher.subscription_interval;
  const is_expired = teacher.is_expired;
  console.log('subscription_plan_tier', subscription_plan_tier);
  let status = 'lower-tier';
  if (
    plan.id === subscription_plan_id &&
    subscriptionType === subscription_interval
  ) {
    status = 'current';
  }
  if (subscription_plan_tier < plan.tier) {
    status = 'upgrade';
  }
  if (
    subscription_plan_tier === plan.tier &&
    intervalMonthsMap[subscriptionType] >
      intervalMonthsMap[subscription_interval]
  ) {
    status = 'upgrade';
  }
  if (is_expired && plan.id === subscription_plan_id) {
    status = 'reactivate';
  }

  planData.status = status;

  return planData as PlanData;
}

function getEnterprisePlanData() {
  return {
    id: 'enterprise-plan',
    monthlyAmount: i18n.t('SIGNUP.LABEL.INQUIRE'),
    displayName: i18n.t('SIGNUP.TITLE.ENTERPRISE'),
    description: i18n.t('SIGNUP.TEXT.CUSTOMIZE_YOUR_COMPLETE_SOLUTION'),
    status: 'upgrade',
    isMostPopular: false,
    hideCoupon: true,
  };
}

export {
  getPlansData,
  getUpgradePlansData,
  getCoachingProgrammePlansData,
  getEnterprisePlanData,
};
